/**
 * A Lottie icon that morphs between two states (e.g. burger <-> X,
 * search <-> X). Replaces the Webflow Lottie player.
 *
 * `open` drives playback: forward to the last frame when true, back to the
 * first frame when false. The frame is tweened with GSAP so the timing matches
 * the original interaction. lottie-web is imported lazily inside the effect so
 * it never runs on the server.
 */
import type { ComponentPropsWithoutRef, FC } from 'react'
import type { AnimationItem } from 'lottie-web'

import { useEffect, useRef } from 'react'

import { gsap } from '@/lib/gsap'

type LottieIconProps = {
    src: string
    open: boolean
    duration?: number
} & ComponentPropsWithoutRef<'div'>

export const LottieIcon: FC<LottieIconProps> = ({
    src,
    open,
    duration = 1,
    ...rest
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const animationRef = useRef<AnimationItem | null>(null)
    const openRef = useRef(open)

    openRef.current = open

    useEffect(() => {
        let animation: AnimationItem | null = null
        let cancelled = false

        import('lottie-web').then((module) => {
            if (cancelled || !containerRef.current) return

            animation = module.default.loadAnimation({
                container: containerRef.current,
                renderer: 'svg',
                loop: false,
                autoplay: false,
                path: src
            })

            animationRef.current = animation

            animation.addEventListener('DOMLoaded', () => {
                if (!animation) return
                const last = Math.max(0, animation.totalFrames - 1)
                animation.goToAndStop(openRef.current ? last : 0, true)
            })
        })

        return () => {
            cancelled = true
            animation?.destroy()
            animationRef.current = null
        }
    }, [src])

    useEffect(() => {
        const animation = animationRef.current
        if (!animation) return

        const run = () => {
            const last = Math.max(0, animation.totalFrames - 1)
            const target = open ? last : 0
            const proxy = { frame: animation.currentFrame }

            gsap.to(proxy, {
                frame: target,
                duration,
                ease: 'none',
                overwrite: true,
                onUpdate: () => animation.goToAndStop(proxy.frame, true)
            })
        }

        if (animation.totalFrames > 0) {
            run()
        } else {
            animation.addEventListener('DOMLoaded', run)
            return () => animation.removeEventListener('DOMLoaded', run)
        }
    }, [open, duration])

    return <div ref={containerRef} {...rest} />
}
